/**
 * printService.js - Motor centralizado de impressão
 * Usa window.open() para criar documentos isolados, eliminando problemas de CSS @media print
 */

const HEADER_HTML = `
<div style="text-align: center; border-bottom: 2px solid black; padding-bottom: 8px; margin-bottom: 12px;">
  <div style="display: flex; align-items: flex-start; justify-content: space-between;">
    <img src="/assets/brasao-sao-domingos.png" alt="Brasao"
      style="width: 56px; height: 56px; object-fit: contain;"
      onerror="this.onerror=null; this.src='https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Brasao-saodomingosdoprata-mg.jpg/120px-Brasao-saodomingosdoprata-mg.jpg';">
    <div style="flex: 1; margin: 0 10px;">
      <h1 style="font-size: 11px; font-weight: bold; margin: 0 0 1px 0;">Fundo Municipal de Saude de Sao Domingos do Prata</h1>
      <h2 style="font-size: 10px; font-weight: bold; margin: 0 0 1px 0;">SECRETARIA MUNICIPAL DE SAUDE E ACAO SOCIAL</h2>
      <p style="font-size: 8px; margin: 0; line-height: 1.2;">Rua Prof. Cristiano Morais, n 38-Centro Sao Domingos do Prata-MG</p>
      <p style="font-size: 8px; margin: 0; line-height: 1.2;">Telefone: (31) 3868-1310</p>
      <p style="font-size: 8px; margin: 0; line-height: 1.2;">E-mail: dmsac@saodomingosdoprata.mg.gov.br</p>
      <p style="font-size: 8px; margin: 0; line-height: 1.2;">CNPJ: 11.466.385/0001-68-Inscricao Estadual: Isento</p>
    </div>
    <img src="/assets/logo-sus.png" alt="Logo SUS"
      style="width: 56px; height: 56px; object-fit: contain;"
      onerror="this.onerror=null; this.src='https://upload.wikimedia.org/wikipedia/commons/thumb/3/39/Logo_SUS.svg/120px-Logo_SUS.svg.png';">
  </div>
</div>`;

function getDateFormatted() {
  return new Date().toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function getFontConfig(medCount) {
  if (medCount <= 3) return { med: '18px', instr: '16px', name: '26px', uso: '18px', spacing: '18px' };
  if (medCount <= 5) return { med: '16px', instr: '14px', name: '22px', uso: '16px', spacing: '14px' };
  if (medCount <= 8) return { med: '14px', instr: '12px', name: '18px', uso: '14px', spacing: '10px' };
  if (medCount <= 12) return { med: '12px', instr: '11px', name: '16px', uso: '12px', spacing: '8px' };
  return { med: '11px', instr: '10px', name: '14px', uso: '11px', spacing: '6px' };
}

function getPackagingInfo(med) {
  if (med.packaging_type === 'uso_continuo') return 'USO CONTINUO';
  if (!med.quantity) return '';
  const qty = parseInt(med.quantity) || 1;
  const types = {
    frascos: qty === 1 ? 'FRASCO' : 'FRASCOS',
    cartelas: qty === 1 ? 'CARTELA' : 'CARTELAS',
    ampolas: qty === 1 ? 'AMPOLA' : 'AMPOLAS',
    tubos: qty === 1 ? 'TUBO' : 'TUBOS',
    caixas: qty === 1 ? 'CX' : 'CXS',
    envelopes: qty === 1 ? 'ENVELOPE' : 'ENVELOPES',
    saches: qty === 1 ? 'SACHÊ' : 'SACHÊS',
    unidades: qty === 1 ? 'UNIDADE' : 'UNIDADES',
    comprimidos: qty === 1 ? 'COMPRIMIDO' : 'COMPRIMIDOS',
  };
  const label = types[med.packaging_type] || (qty === 1 ? 'CP' : 'CPS');
  return `${med.quantity} ${label}`;
}

function buildMedicationGroups(prescription) {
  if (prescription.medication_groups && prescription.medication_groups.length > 0) {
    return prescription.medication_groups;
  }
  const groups = {};
  for (const med of prescription.medicamentos || []) {
    const via = med.via || prescription.uso || 'USO ORAL';
    if (!groups[via]) groups[via] = [];
    groups[via].push(med);
  }
  return Object.entries(groups).map(([via, meds]) => ({ via, medicamentos: meds }));
}

function buildPrescriptionPageHTML(patient, prescription, via, opts = {}) {
  const { showQRCode = false, validationCode = '', validationUrl = '', isAvulsa = false } = opts;
  const groups = buildMedicationGroups(prescription);
  const totalMeds = prescription.medicamentos?.length || 0;
  const fc = getFontConfig(totalMeds);
  const currentDate = getDateFormatted();
  const viaText = via ? `${via}a VIA` : '';

  let medicationsHTML = '';
  groups.forEach((group, gi) => {
    medicationsHTML += `
      <div style="${gi > 0 ? 'margin-top: 20px; padding-top: 12px;' : ''}">
        <div style="text-align: center; margin-bottom: 12px;">
          <h3 style="font-size: ${fc.uso}; font-weight: bold; text-transform: uppercase; text-decoration: underline; margin: 0;">
            ${group.via}
          </h3>
        </div>`;
    group.medicamentos.forEach((med, mi) => {
      const dosage = med.dosage ? `${med.dosage} ${(med.dosage_unit || 'MG').toLowerCase()}` : '';
      const packaging = getPackagingInfo(med);
      medicationsHTML += `
        <div style="margin-bottom: ${fc.spacing};">
          <div style="display: flex; align-items: baseline; gap: 8px;">
            <span style="font-size: ${fc.med}; font-weight: bold; white-space: nowrap;">${mi + 1})</span>
            <span style="font-size: ${fc.med}; font-weight: bold; flex: 1; word-break: break-word;">
              ${(med.name || '').toUpperCase()}${dosage ? ' ' + dosage : ''}
            </span>
            ${packaging ? `<span style="font-size: ${fc.med}; font-weight: bold; white-space: nowrap; text-align: right;">
              &mdash;&mdash;&mdash;&mdash;&mdash; ${packaging}
            </span>` : ''}
          </div>
          ${med.instructions ? `<p style="font-size: ${fc.instr}; margin: 3px 0 0 24px; font-weight: bold; text-transform: uppercase;">${med.instructions}</p>` : ''}
        </div>`;
    });
    medicationsHTML += `</div>`;
  });

  // Dates section
  let datesHTML = '';
  if (!isAvulsa) {
    const renovacao = prescription.data_impressao
      ? new Date(prescription.data_impressao).toLocaleDateString('pt-BR')
      : new Date().toLocaleDateString('pt-BR');
    const proxRenovacao = prescription.data_validade
      ? new Date(prescription.data_validade).toLocaleDateString('pt-BR')
      : 'N/A';
    datesHTML = `
      <div style="display: flex; justify-content: space-between; font-size: 11px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 6px; margin-bottom: 10px;">
        <div><strong>Renovacao:</strong> ${renovacao}</div>
        <div><strong>Proxima Renovacao:</strong> ${proxRenovacao}</div>
      </div>`;
  }

  // QR Code section
  let qrHTML = '';
  if (showQRCode && validationCode) {
    const qrUrl = validationUrl || validationCode;
    qrHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="text-align: right; font-size: 10px;">
          <p style="color: #333; margin: 0 0 2px 0; font-weight: bold;">${isAvulsa ? 'Receita Avulsa:' : 'Validar receita:'}</p>
          <p style="font-family: monospace; font-weight: bold; font-size: 9px; margin: 0; color: #555;">${validationCode}</p>
        </div>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&format=png&data=${encodeURIComponent(qrUrl)}"
          style="width: 55px; height: 55px; border: 1px solid #ccc;"
          alt="QR Code"
          crossorigin="anonymous">
      </div>`;
  } else {
    qrHTML = `<div style="text-align: right; font-size: 10px; color: #999;">${isAvulsa ? 'Receita Avulsa' : ''}</div>`;
  }

  // Prescription name
  const nameHTML = prescription.name ? `<p style="text-align: center; font-size: 12px; color: #666; margin: 4px 0 0 0; font-style: italic;">${prescription.name}</p>` : '';

  return `
    <div style="width: 100%; max-width: 210mm; padding: 6mm 10mm; box-sizing: border-box; display: flex; flex-direction: column; position: relative; min-height: 281mm; max-height: 281mm; overflow: hidden; background: white; margin: 0 auto; font-family: Arial, sans-serif; color: black;">
      ${viaText ? `<div style="position: absolute; top: 4px; right: 8px; font-size: 10px; font-weight: bold; color: #444;">${viaText}</div>` : ''}
      ${HEADER_HTML}
      <div style="margin-bottom: 12px; text-align: center;">
        <h2 style="font-size: ${fc.name}; font-weight: bold; text-transform: uppercase; margin: 0;">
          <span style="font-weight: 600;">NOME: </span>${patient.nome || ''}
        </h2>
        ${nameHTML}
      </div>
      ${datesHTML}
      <div style="flex-grow: 1; margin-top: 8px;">
        ${medicationsHTML}
      </div>
      <div style="padding-top: 10px; margin-top: auto; border-top: 1px solid black;">
        <div style="display: flex; justify-content: space-between; align-items: flex-end;">
          <div style="text-align: left;">
            <p style="font-size: 13px; font-weight: bold; margin: 0;">Sao Domingos do Prata</p>
            <p style="font-size: 13px; font-weight: bold; margin: 0;">${currentDate}</p>
          </div>
          ${qrHTML}
        </div>
      </div>
    </div>`;
}

const BACKEND_URL = typeof window !== 'undefined' ? process.env.REACT_APP_BACKEND_URL : '';

/**
 * Generate a validation code for a prescription
 */
function generateValidationCode(prescriptionId, isAvulsa) {
  if (isAvulsa) {
    const ts = Date.now().toString(36).toUpperCase().slice(-8);
    return `AVL-${ts}`;
  }
  if (prescriptionId) {
    return prescriptionId.substring(0, 12).toUpperCase();
  }
  return '';
}

/**
 * Print prescriptions in a new window
 * @param {Array} items - Array of { patient, prescription } objects
 * @param {Object} opts - Options: { showQRCode, validationCodes, isAvulsa }
 */
export function printPrescriptions(items, opts = {}) {
  if (!items || items.length === 0) return;

  let pagesHTML = '';
  items.forEach(({ patient, prescription }, index) => {
    // Auto-generate validation code if QR is enabled but codes not provided
    let valCode = opts.validationCodes?.[prescription.id] || '';
    let valUrl = opts.validationUrls?.[prescription.id] || '';

    if (opts.showQRCode && !valCode) {
      valCode = generateValidationCode(prescription.id, opts.isAvulsa);
      valUrl = prescription.id ? `${BACKEND_URL}/validar/${prescription.id}` : '';
    }

    const itemOpts = {
      ...opts,
      validationCode: valCode,
      validationUrl: valUrl,
    };
    // 1st copy
    pagesHTML += `<div class="page">${buildPrescriptionPageHTML(patient, prescription, 1, itemOpts)}</div>`;
    // 2nd copy
    pagesHTML += `<div class="page${index === items.length - 1 ? ' last' : ''}">${buildPrescriptionPageHTML(patient, prescription, 2, itemOpts)}</div>`;
  });

  openPrintWindow(pagesHTML, 'Receita');
}

/**
 * Print a medical document in a new window (with QR code)
 */
export function printDocument(docType, content, patientName, docId) {
  const typeLabels = {
    atestado: 'ATESTADO MEDICO',
    declaracao: 'DECLARACAO DE COMPARECIMENTO',
    laudo: 'LAUDO MEDICO',
    encaminhamento: 'ENCAMINHAMENTO',
    relatorio: 'RELATORIO MEDICO',
    receita_especial: 'RECEITA ESPECIAL',
    outros: 'DOCUMENTO',
  };
  const label = typeLabels[docType] || 'DOCUMENTO';

  // Generate QR code section
  let qrSection = '';
  if (docId) {
    const validationUrl = `${BACKEND_URL}/api/validar-documento/${docId}`;
    qrSection = `
      <div style="display: flex; align-items: center; gap: 8px; margin-top: 10px;">
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&format=png&data=${encodeURIComponent(validationUrl)}"
          style="width: 55px; height: 55px; border: 1px solid #ccc;"
          alt="QR Code" crossorigin="anonymous">
        <div style="font-size: 9px; color: #666;">
          <p style="margin: 0; font-weight: bold;">Documento com validacao digital</p>
          <p style="margin: 0; font-family: monospace;">${docId.substring(0, 12).toUpperCase()}</p>
        </div>
      </div>`;
  } else {
    // Generate a validation code even without ID
    const ts = Date.now().toString(36).toUpperCase();
    qrSection = `
      <div style="display: flex; align-items: center; gap: 8px; margin-top: 10px;">
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&format=png&data=${encodeURIComponent(`${BACKEND_URL}/validar-documento/${ts}`)}"
          style="width: 55px; height: 55px; border: 1px solid #ccc;"
          alt="QR Code" crossorigin="anonymous">
        <div style="font-size: 9px; color: #666;">
          <p style="margin: 0; font-weight: bold;">Documento com validacao digital</p>
          <p style="margin: 0; font-family: monospace;">DOC-${ts.slice(-8)}</p>
        </div>
      </div>`;
  }

  const pageHTML = `
    <div style="width: 210mm; min-height: 297mm; padding: 20mm; font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6; background: white; color: black; box-sizing: border-box; margin: 0 auto;">
      ${HEADER_HTML}
      <h2 style="text-align: center; font-size: 16pt; text-decoration: underline; margin: 20px 0 30px 0;">${label}</h2>
      <div style="white-space: pre-wrap; text-align: justify; margin-bottom: 50px;">${content}</div>
      <div style="margin-top: auto; padding-top: 30px; border-top: 1px solid black;">
        <div style="display: flex; justify-content: space-between; align-items: flex-end;">
          <div>
            <p style="font-weight: bold; font-size: 14px; margin: 0;">Sao Domingos do Prata</p>
            <p style="font-weight: bold; font-size: 14px; margin: 0;">${getDateFormatted()}</p>
          </div>
          <div style="text-align: center;">
            <div style="border-top: 1px solid black; width: 200px; margin-bottom: 4px;"></div>
            <p style="font-size: 12px; margin: 0;">Assinatura e Carimbo do Profissional</p>
          </div>
        </div>
        ${qrSection}
      </div>
    </div>`;

  openPrintWindow(`<div class="page last">${pageHTML}</div>`, `Documento - ${patientName}`);
}

function openPrintWindow(bodyContent, title) {
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Erro: Popup bloqueado. Por favor, permita popups para este site.');
    return;
  }

  const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <title>${title || 'Impressao'}</title>
  <meta charset="UTF-8">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; background: #f0f0f0; }
    .page { background: white; margin: 10px auto; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
    .page:not(.last) { page-break-after: always; }
    @media print {
      @page { size: A4 portrait; margin: 8mm; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      body { background: white; margin: 0; padding: 0; }
      .page { box-shadow: none; margin: 0; }
      .page:not(.last) { page-break-after: always; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="no-print" style="background: #0f766e; color: white; padding: 10px 20px; text-align: center; font-size: 14px; position: sticky; top: 0; z-index: 100;">
    <button onclick="window.print()" style="background: white; color: #0f766e; border: none; padding: 8px 24px; border-radius: 20px; font-weight: bold; cursor: pointer; font-size: 14px; margin-right: 10px;">
      Imprimir
    </button>
    <button onclick="window.close()" style="background: transparent; color: white; border: 1px solid white; padding: 8px 24px; border-radius: 20px; cursor: pointer; font-size: 14px;">
      Fechar
    </button>
  </div>
  ${bodyContent}
  <script>
    function waitAndPrint() {
      var imgs = document.querySelectorAll('img');
      var loaded = 0;
      var total = imgs.length;
      if (total === 0) { setTimeout(function() { window.print(); }, 300); return; }
      imgs.forEach(function(img) {
        if (img.complete) { loaded++; if (loaded >= total) setTimeout(function() { window.print(); }, 300); }
        else {
          img.onload = function() { loaded++; if (loaded >= total) setTimeout(function() { window.print(); }, 300); };
          img.onerror = function() { loaded++; if (loaded >= total) setTimeout(function() { window.print(); }, 300); };
        }
      });
    }
    setTimeout(waitAndPrint, 500);
  </script>
</body>
</html>`;

  printWindow.document.write(fullHTML);
  printWindow.document.close();
}
